import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Building,
  MapPin,
  Users,
  ArrowRight,
  Search,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_LABEL = {
  active: "Active",
  draft: "Open to New Members",
};

const MarketplacePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/content/marketplace/listings`)
      .then((res) => {
        if (!cancelled) setListings(res.data || []);
      })
      .catch(() => {
        if (!cancelled) setError("Unable to load marketplace listings.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return listings.filter((l) => {
      if (statusFilter !== "all" && l.status !== statusFilter) return false;
      if (!q) return true;
      return (
        (l.name || "").toLowerCase().includes(q) ||
        (l.location || "").toLowerCase().includes(q)
      );
    });
  }, [listings, search, statusFilter]);

  const handleListLand = () => {
    if (!user) {
      navigate("/register");
      return;
    }
    if (user.role === "landowner" || user.role === "consortium_rep") {
      navigate("/landowner");
    } else {
      navigate("/dashboard");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="marketplace-page">
      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-r from-[#4E80FF] to-[#2952CC] py-16">
        <div className="container-main">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm text-white/90 mb-6">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
            Delhi &middot; DDA Land Pooling Policy 2018
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
            Land Pooling Marketplace
          </h1>
          <p className="text-xl text-white/90 max-w-2xl">
            Browse verified Delhi consortiums pooling land under DDA&apos;s
            notified zones. Developers can shortlist projects; landowners can
            join open pools.
          </p>
        </div>
      </section>

      {/* Search & Filter */}
      <section className="py-8 bg-white border-b">
        <div className="container-main">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search by location, project name or zone..."
                className="pl-10"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="marketplace-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[220px]" data-testid="marketplace-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="draft">Open to New Members</SelectItem>
              </SelectContent>
            </Select>
            <Button
              className="btn-primary"
              onClick={handleListLand}
              data-testid="list-land-btn"
            >
              List Your Land
            </Button>
          </div>
        </div>
      </section>

      {/* Listings */}
      <section className="py-12">
        <div className="container-main">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-heading text-2xl font-bold text-slate-900">
                Active Projects
              </h2>
              <p className="text-slate-600" data-testid="marketplace-count">
                {filtered.length} {filtered.length === 1 ? "project" : "projects"} available
              </p>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary-700 border-t-transparent" />
            </div>
          ) : error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 p-6 text-sm">
              {error}
            </div>
          ) : filtered.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-16 text-center">
                <Building className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-slate-500">
                  No projects match your filters yet.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {filtered.map((listing) => {
                const progress = listing.progress ?? 0;
                return (
                  <Card
                    key={listing.id}
                    className="card-hover overflow-hidden"
                    data-testid={`listing-card-${listing.id}`}
                  >
                    <CardContent className="p-0">
                      <div
                        className="h-2"
                        style={{
                          background:
                            listing.status === "active"
                              ? "linear-gradient(90deg, #4E80FF, #2952CC)"
                              : "#F59E0B",
                        }}
                      />
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-3 gap-3">
                          <div className="min-w-0">
                            <h3 className="font-heading text-lg font-semibold text-slate-900">
                              {listing.name}
                            </h3>
                            <div className="flex items-center gap-1 text-sm text-slate-500 mt-0.5">
                              <MapPin className="w-4 h-4 shrink-0" />
                              <span className="truncate">{listing.location}</span>
                            </div>
                          </div>
                          <Badge
                            variant="outline"
                            className={
                              listing.status === "active"
                                ? "bg-[#4E80FF] text-white border-transparent"
                                : "border-amber-300 text-amber-700"
                            }
                          >
                            {STATUS_LABEL[listing.status] || listing.status}
                          </Badge>
                        </div>

                        {listing.description && (
                          <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                            {listing.description}
                          </p>
                        )}

                        <div className="grid grid-cols-3 gap-4 mb-4">
                          <div>
                            <p className="text-xs text-slate-500">Pooled</p>
                            <p className="font-semibold text-slate-900">
                              {listing.pooled_area_acres || 0} ac
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Members</p>
                            <p className="font-semibold text-slate-900 flex items-center gap-1">
                              <Users className="w-4 h-4" />
                              {listing.members_count || 0}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Type</p>
                            <p className="font-semibold text-slate-900 capitalize">
                              {listing.predominant_type || "Mixed"}
                            </p>
                          </div>
                        </div>

                        {progress != null && (
                          <div className="mb-4">
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="text-slate-500">
                                Progress towards target
                              </span>
                              <span className="font-medium">{progress}%</span>
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-2">
                              <div
                                className="bg-[#4E80FF] h-2 rounded-full transition-all"
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                          </div>
                        )}

                        <Button
                          className="w-full btn-secondary"
                          onClick={() => {
                            if (!user) {
                              navigate("/login");
                              return;
                            }
                            navigate(`/consortium/${listing.id}`);
                          }}
                          data-testid={`view-project-${listing.id}`}
                        >
                          View Details
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-900">
        <div className="container-main text-center">
          <h2 className="font-heading text-3xl font-bold text-white mb-4">
            Have Land in a DDA-Notified Zone?
          </h2>
          <p className="text-slate-300 mb-8 max-w-xl mx-auto">
            List your parcel and connect with existing Delhi consortiums or start
            your own under the Land Pooling Policy 2018.
          </p>
          <Button
            size="lg"
            className="bg-[#4E80FF] hover:bg-[#2952CC]"
            onClick={handleListLand}
            data-testid="cta-list-land-btn"
          >
            Get Started
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MarketplacePage;
